import { HasIdAndName, useComputed } from "@homebound/beam";
import { useMemo } from "react";
import { ListFieldState, ObjectState } from "src/utils/formState";
import { AiFieldProposalFormValue } from "./aiFieldProposalForm";
import { AiProposalParser, findAiFieldProposal, parseAiNamedOption } from "./bindAiProposal";

/**
 * The `ObjectState` for the AI proposal for `fieldName`, if the agent proposed one.
 */
export function useAiFieldProposalState(
  fieldProposals: ListFieldState<AiFieldProposalFormValue> | undefined,
  fieldName: string,
): ObjectState<AiFieldProposalFormValue> | undefined {
  return useComputed(() => findAiFieldProposal(fieldProposals?.rows ?? [], fieldName), [fieldProposals, fieldName]);
}

/** A proposal and the parser that reads it, so callers can use both. */
export type AiNamedOptionProposal = {
  state: ObjectState<AiFieldProposalFormValue> | undefined;
  parser: AiProposalParser<string>;
};

/**
 * The proposal for a select whose field holds an option's *id* but whose proposal names the option.
 */
export function useAiNamedOptionProposal(
  fieldProposals: ListFieldState<AiFieldProposalFormValue> | undefined,
  fieldName: string,
  options: readonly HasIdAndName[],
): AiNamedOptionProposal {
  const state = useAiFieldProposalState(fieldProposals, fieldName);
  const parser = useMemo(() => parseAiNamedOption(options), [options]);
  return useMemo(() => ({ state, parser }), [state, parser]);
}
